import React, {useEffect, useState} from 'react'
import {Panel, Tag, Grid, Row, Col, Message} from 'rsuite'
import {postData} from '../utility'
import {OverlayLoader} from '../atoms/loader'
import {useTranslation} from 'react-i18next'

interface Tenant {
  _id: string
  name: string
  forceSingleGroup?: boolean
  active: boolean
  [key: string]: any
}

interface TenantGroup {
  groupId: number
  tenantIds: string[]
  certName: string
}

const MAX_GROUP_SIZE = 5

export function TenantGroups() {
  const {t} = useTranslation()

  const [tenants, setTenants] = useState<Tenant[]>([])
  const [tenantGroups, setTenantGroups] = useState<TenantGroup[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await postData('/getTenantGroups', '')
        const parsed = typeof response.data === 'string' ? JSON.parse(response.data) : response.data
        const {tenants, tenantGroups} = parsed.data || {}

        setTenants(tenants ?? [])
        setTenantGroups(tenantGroups ?? [])
      } catch (err) {
        console.error(err)
        setError('Failed to fetch tenant groups')
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [])

  if (loading) return <OverlayLoader />
  if (error) {
    return (
      <Message type="error" showIcon>
        {error}
      </Message>
    )
  }

  const tenantsById = Object.fromEntries(tenants.map((t) => [t._id, t]))

  // Filter shared groups (with >1 tenant and not forceSingleGroup)
  const sharedGroups = tenantGroups.filter((group) => {
    const tenantsInGroup = group.tenantIds.map((id) => tenantsById[id]).filter(Boolean)
    return tenantsInGroup.length > 1 && !tenantsInGroup.some((t) => t.forceSingleGroup)
  })

  // Get IDs used in shared groups
  const groupedTenantIds = new Set(sharedGroups.flatMap((g) => g.tenantIds))

  // Single-tenant certs: either marked with forceSingleGroup or not grouped
  const singleTenantCerts = tenants.filter(
    (t) => t.active && (t.forceSingleGroup || !groupedTenantIds.has(t._id))
  )

  const totalCerts = sharedGroups.length + singleTenantCerts.length
  const certLimit = 15
  const isNearLimit = totalCerts >= certLimit - 2
  const isOverLimit = totalCerts >= certLimit

  return (
    <>
      <h2 style={{paddingBottom: 10}}>{t('navbar.tenantGroups')}</h2>
      <div
        style={{
          fontSize: '1.25rem',
          fontWeight: 700,
          paddingBottom: 30,
          color: isOverLimit ? '#d32f2f' : isNearLimit ? '#ed6c02' : '#2e7d32', // red / orange / green
        }}
      >
        Certs: {totalCerts} / {certLimit}
        {isOverLimit && <span style={{marginLeft: 10, fontWeight: 600}}>⚠️ Limit Reached</span>}
        {!isOverLimit && isNearLimit && (
          <span style={{marginLeft: 10, fontWeight: 500}}>⚠️ Near Limit</span>
        )}
      </div>
      <Grid fluid style={{margin: 0, display: 'flex', flexDirection: 'column', width: '100%'}}>
        <Row gutter={24} style={{width: '100%', marginBottom: 8}}>
          {/* Shared group certs */}
          {sharedGroups.map((group, index) => (
            <Col md={10} lg={7} key={`group-${group.groupId}`} style={{paddingBottom: 24}}>
              <Panel bordered header={`Group ${index + 1}`}>
                <Tag color="blue" style={{marginBottom: 8}}>
                  {group.certName}
                </Tag>
                {Array.from({length: MAX_GROUP_SIZE}).map((_, idx) => {
                  const tenantId = group.tenantIds[idx]
                  const tenant = tenantId ? tenantsById[tenantId] : null
                  return (
                    <div key={idx} style={{padding: '4px 0'}}>
                      {tenant ? (
                        <Tag color="green">✅ {tenant.name}</Tag>
                      ) : (
                        <Tag color="orange">⬜ Empty Slot</Tag>
                      )}
                    </div>
                  )
                })}
              </Panel>
            </Col>
          ))}

          {/* Single tenant certs */}
          {singleTenantCerts.map((tenant) => (
            <Col md={10} lg={7} key={`single-${tenant._id}`} style={{paddingBottom: 24}}>
              <Panel bordered header={`Single Tenant`}>
                <Tag color="blue" style={{marginBottom: 8}}>{`${tenant.name}-cert`}</Tag>
                <Tag color="green">✅ {tenant.name}</Tag>
              </Panel>
            </Col>
          ))}
        </Row>
      </Grid>
    </>
  )
}
